import { Box, Tooltip, Typography } from "@mui/material";
import SyncIcon from "@mui/icons-material/Sync";
import { useCallback, useEffect, useState } from "react";
import { fetchSyncUsage, SYNC_USAGE_CHANGED_EVENT, SyncUsageDTO } from "../../../services/sync";
import { colors } from "../../../theme";

/**
 * How many syncs the account has left today, in the header.
 *
 * Refreshed on mount and whenever a sync commits (SYNC_USAGE_CHANGED_EVENT) rather than
 * polled - the number only moves as a result of something this user did, so there's
 * nothing to discover on a timer.
 */
export default function SyncQuota() {
    const [usage, setUsage] = useState<SyncUsageDTO | null>(null);

    const load = useCallback(async () => {
        try {
            setUsage(await fetchSyncUsage());
        } catch {
            // A failed lookup shouldn't put an error in the header - just show nothing.
            setUsage(null);
        }
    }, []);

    useEffect(() => {
        load();
        window.addEventListener(SYNC_USAGE_CHANGED_EVENT, load);
        return () => window.removeEventListener(SYNC_USAGE_CHANGED_EVENT, load);
    }, [load]);

    if (!usage) {
        return null;
    }

    const exhausted = usage.remaining === 0;
    const low = usage.remaining > 0 && usage.remaining <= 5;
    const color = exhausted ? colors.danger : low ? "#F5A623" : colors.textSecondary;

    return (
        <Tooltip
            title={
                exhausted
                    ? `You've used all ${usage.limit} of today's syncs. Your allowance resets at midnight.`
                    : `${usage.remaining} of ${usage.limit} syncs left today. Every account gets ${usage.limit} a day, and only a sync you confirm counts — previewing is free.`
            }
            placement="bottom"
        >
            <Box
                aria-label={`${usage.remaining} of ${usage.limit} syncs left today`}
                sx={{
                    display: { xs: "none", sm: "flex" },
                    alignItems: "center",
                    gap: 0.5,
                    px: 1.25,
                    py: 0.5,
                    borderRadius: 500,
                    border: `1px solid ${colors.line}`,
                    backgroundColor: colors.elevatedHover,
                    cursor: "default",
                }}
            >
                <SyncIcon sx={{ fontSize: 16, color }} />
                <Typography sx={{ fontSize: 13, fontWeight: 700, color, lineHeight: 1 }}>
                    {usage.remaining}
                    <Box component="span" sx={{ color: colors.textMuted, fontWeight: 500 }}>
                        /{usage.limit}
                    </Box>
                </Typography>
            </Box>
        </Tooltip>
    );
}
